import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatCurrency, formatDate, formatDateLong } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Package, Plus, Search, Pencil, Trash2, ArrowLeft, ChevronLeft, ChevronRight, Send, X, Download, Loader2, Truck, Check, Clock, XCircle, PackageCheck, MailOpen, FileText, Receipt, Upload, ExternalLink, Mail } from "lucide-react";
import { toast } from "sonner";

const statusColors = {
  Draft: "bg-gray-100 text-gray-700 border-gray-300",
  Sent: "bg-blue-50 text-blue-700 border-blue-200",
  "Partially Fulfilled": "bg-amber-50 text-amber-700 border-amber-200",
  Fulfilled: "bg-green-50 text-green-700 border-green-200",
  Cancelled: "bg-red-50 text-red-500 border-red-200 line-through",
};

const statusIcons = { Draft: Pencil, Sent: Send, "Partially Fulfilled": Truck, Fulfilled: PackageCheck, Cancelled: XCircle };

export default function PurchaseOrdersPage() {
  const { id } = useParams();
  if (id) return <PODetail poId={id} />;
  return <POList />;
}

function POList() {
  const [pos, setPOs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [selected, setSelected] = useState([]);
  const [pdfLoading, setPdfLoading] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const canManage = ["admin", "sales_manager"].includes(user?.role);

  const fetchPOs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (search) params.set("search", search);
      if (statusFilter && statusFilter !== "all") params.set("status", statusFilter);
      const res = await api.get(`/purchase-orders?${params}`);
      setPOs(res.data.purchase_orders);
      setTotalPages(res.data.total_pages);
      setStats(res.data.stats);
    } catch { toast.error("Failed to load purchase orders"); }
    setLoading(false);
  }, [search, statusFilter, page]);

  useEffect(() => {
    const s = searchParams.get("status");
    if (s) setStatusFilter(s);
  }, [searchParams]);

  useEffect(() => { fetchPOs(); }, [fetchPOs]);

  const handleDelete = async () => {
    try {
      await api.delete(`/purchase-orders/${deleteId}`);
      toast.success("Purchase order deleted");
      setDeleteId(null);
      fetchPOs();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to delete"); }
  };

  const handleBulkPdf = async () => {
    if (!selected.length) return;
    setPdfLoading(true);
    try {
      const res = await api.post("/purchase-orders/bulk-pdf", { po_ids: selected }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = "purchase_orders.zip"; a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${selected.length} POs`);
    } catch { toast.error("Failed to download"); }
    setPdfLoading(false);
  };

  const toggleSelect = (id) => setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const toggleAll = () => setSelected(selected.length === pos.length ? [] : pos.map((p) => p.po_id));

  return (
    <div data-testid="purchase-orders-page" className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#1F2937]">Purchase Orders</h1>
          <div className="flex flex-wrap items-center gap-3 mt-1">
            <Badge className="bg-gray-100 text-gray-600 rounded-full text-xs">{stats.draft || 0} Draft</Badge>
            <Badge className="bg-blue-50 text-blue-700 rounded-full text-xs">{stats.sent || 0} Sent</Badge>
            <Badge className="bg-amber-50 text-amber-700 rounded-full text-xs">{stats.partially_fulfilled || 0} Partial</Badge>
            <Badge className="bg-green-50 text-green-700 rounded-full text-xs">{stats.fulfilled || 0} Fulfilled</Badge>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {selected.length > 0 && (
            <Button data-testid="bulk-pdf-btn" variant="outline" size="sm" onClick={handleBulkPdf} disabled={pdfLoading} className="rounded-sm">
              {pdfLoading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />} Download {selected.length} PDF{selected.length > 1 ? "s" : ""}
            </Button>
          )}
          {canManage && (
            <Button data-testid="create-po-button" onClick={() => setDialogOpen(true)} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">
              <Plus className="w-4 h-4 mr-1" /> New Purchase Order
            </Button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input data-testid="po-search" placeholder="Search by PO # or supplier..." value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="pl-9 rounded-sm border-gray-300 focus:ring-[#E63946]" />
        </div>
        <Select data-testid="po-status-filter" value={statusFilter || "all"} onValueChange={(v) => { setStatusFilter(v === "all" ? "" : v); setPage(1); }}>
          <SelectTrigger className="w-[180px] rounded-sm"><SelectValue placeholder="All Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {["Draft", "Sent", "Partially Fulfilled", "Fulfilled", "Cancelled"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="border border-gray-200 rounded-sm shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50/50">
                <th className="p-3 w-10"><input type="checkbox" checked={selected.length === pos.length && pos.length > 0} onChange={toggleAll} className="rounded border-gray-300" /></th>
                <th className="p-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">PO #</th>
                <th className="p-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Supplier</th>
                <th className="p-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Date</th>
                <th className="p-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Expected</th>
                <th className="p-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">Total</th>
                <th className="p-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Status</th>
                <th className="p-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-gray-400" /></td></tr>
              ) : pos.length === 0 ? (
                <tr><td colSpan={8} className="p-8 text-center text-gray-500">No purchase orders found</td></tr>
              ) : pos.map((po) => {
                const isOverdue = po.status === "Sent" && po.expected_delivery_date && po.expected_delivery_date < new Date().toISOString().split("T")[0];
                return (
                  <tr key={po.po_id} className={`border-b border-gray-100 hover:bg-gray-50/50 transition-colors ${isOverdue ? "bg-red-50/30" : ""}`}>
                    <td className="p-3"><input type="checkbox" checked={selected.includes(po.po_id)} onChange={() => toggleSelect(po.po_id)} className="rounded border-gray-300" /></td>
                    <td className="p-3 cursor-pointer" onClick={() => navigate(`/purchase-orders/${po.po_id}`)}>
                      <span className="font-mono font-semibold text-[#E63946] hover:underline text-sm">{po.po_number}</span>
                    </td>
                    <td className="p-3 max-w-[200px]"><span className="text-gray-700 break-words">{po.supplier_name}</span></td>
                    <td className="p-3 text-gray-500 text-xs">{formatDateLong(po.po_date)}</td>
                    <td className="p-3 text-xs">
                      {po.expected_delivery_date ? (
                        <span className={isOverdue ? "text-red-600 font-semibold" : "text-gray-500"}>
                          {formatDate(po.expected_delivery_date)} {isOverdue && "⚠"}
                        </span>
                      ) : <span className="text-gray-400">-</span>}
                    </td>
                    <td className="p-3 text-right font-semibold text-sm">{formatCurrency(po.total)}</td>
                    <td className="p-3">
                      <Badge className={`${statusColors[po.status] || ""} text-xs rounded-full border px-2 py-0.5`}>{po.status}</Badge>
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-400 hover:text-gray-600"
                          onClick={async () => { try { const r = await api.get(`/purchase-orders/${po.po_id}/pdf`, { responseType: "blob" }); const u = window.URL.createObjectURL(new Blob([r.data])); const a = document.createElement("a"); a.href = u; a.download = `${po.po_number}.pdf`; a.click(); } catch { toast.error("PDF failed"); } }}>
                          <Download className="w-3.5 h-3.5" />
                        </Button>
                        {po.status === "Draft" && canManage && (
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-400 hover:text-[#E63946]" onClick={() => setDeleteId(po.po_id)}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} className="rounded-sm"><ChevronLeft className="w-4 h-4" /></Button>
          <span className="text-xs text-gray-500">Page {page} of {totalPages}</span>
          <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="rounded-sm"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      )}

      {dialogOpen && <CreatePODialog open={dialogOpen} onClose={() => setDialogOpen(false)} onCreated={(po) => { setDialogOpen(false); navigate(`/purchase-orders/${po.po_id}`); }} />}

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete Purchase Order?</AlertDialogTitle><AlertDialogDescription>This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ==================== Create PO Dialog ====================

function CreatePODialog({ open, onClose, onCreated }) {
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({
    supplier_id: "", po_date: new Date().toISOString().split("T")[0],
    expected_delivery_date: "", delivery_address: "", delivery_instructions: "",
    payment_terms: "", notes_to_supplier: "", internal_notes: "", vat_rate: 15,
  });
  const [items, setItems] = useState([{ description: "", sku: "", product_id: "", quantity: 1, unit_cost: 0 }]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/suppliers?limit=200").then((r) => setSuppliers(r.data.suppliers));
    api.get("/products?limit=500").then((r) => setProducts(r.data.products));
  }, []);

  const addItem = () => setItems([...items, { description: "", sku: "", product_id: "", quantity: 1, unit_cost: 0 }]);
  const removeItem = (idx) => items.length > 1 && setItems(items.filter((_, i) => i !== idx));
  const updateItem = (idx, field, val) => {
    const updated = [...items];
    updated[idx] = { ...updated[idx], [field]: val };
    if (field === "product_id" && val) {
      const p = products.find((x) => x.product_id === val);
      if (p) { updated[idx].description = p.product_name || p.name || ""; updated[idx].sku = p.sku; updated[idx].unit_cost = p.cost_price || 0; }
    }
    setItems(updated);
  };

  const subtotal = items.reduce((s, i) => s + (parseFloat(i.quantity) || 0) * (parseFloat(i.unit_cost) || 0), 0);
  const vatAmount = subtotal * (parseFloat(form.vat_rate) || 0) / 100;
  const total = subtotal + vatAmount;

  const handleSubmit = async () => {
    if (!form.supplier_id) { toast.error("Select a supplier"); return; }
    if (items.every((i) => !i.description)) { toast.error("Add at least one item"); return; }
    setSaving(true);
    try {
      const res = await api.post("/purchase-orders", { ...form, line_items: items });
      toast.success(`${res.data.po_number} created`);
      onCreated(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create PO"); }
    setSaving(false);
  };

  const selectedSupplier = suppliers.find((s) => s.supplier_id === form.supplier_id);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto rounded-sm">
        <DialogHeader><DialogTitle className="font-heading text-lg">New Purchase Order</DialogTitle></DialogHeader>
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold uppercase text-gray-500 mb-1 block">Supplier *</label>
              <Select data-testid="po-supplier-select" value={form.supplier_id} onValueChange={(v) => {
                const sup = suppliers.find((s) => s.supplier_id === v);
                setForm({ ...form, supplier_id: v, payment_terms: sup?.payment_terms || form.payment_terms });
              }}>
                <SelectTrigger className="rounded-sm"><SelectValue placeholder="Select supplier" /></SelectTrigger>
                <SelectContent>{suppliers.map((s) => <SelectItem key={s.supplier_id} value={s.supplier_id}>{s.company_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase text-gray-500 mb-1 block">PO Date</label>
              <Input type="date" value={form.po_date} onChange={(e) => setForm({ ...form, po_date: e.target.value })} className="rounded-sm" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase text-gray-500 mb-1 block">Expected Delivery Date</label>
              <Input type="date" value={form.expected_delivery_date} onChange={(e) => setForm({ ...form, expected_delivery_date: e.target.value })} className="rounded-sm" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase text-gray-500 mb-1 block">Payment Terms</label>
              <Input value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} placeholder={selectedSupplier?.payment_terms || "e.g. Net 30"} className="rounded-sm" />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase text-gray-500 mb-2 block">Line Items</label>
            <div className="border border-gray-200 rounded-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead><tr className="bg-gray-50 border-b">
                  <th className="p-2 text-left text-xs text-gray-500 w-[30%]">Product / Description</th>
                  <th className="p-2 text-left text-xs text-gray-500 w-[15%]">SKU</th>
                  <th className="p-2 text-center text-xs text-gray-500 w-[12%]">Qty</th>
                  <th className="p-2 text-right text-xs text-gray-500 w-[15%]">Unit Cost</th>
                  <th className="p-2 text-right text-xs text-gray-500 w-[15%]">Total</th>
                  <th className="p-2 w-[8%]"></th>
                </tr></thead>
                <tbody>
                  {items.map((item, idx) => (
                    <tr key={idx} className="border-b border-gray-100">
                      <td className="p-2">
                        <Select value={item.product_id || "_custom"} onValueChange={(v) => updateItem(idx, "product_id", v === "_custom" ? "" : v)}>
                          <SelectTrigger className="rounded-sm h-8 text-xs"><SelectValue placeholder="Select or custom" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_custom">Custom Item</SelectItem>
                            {products.map((p) => <SelectItem key={p.product_id} value={p.product_id}>{p.sku} — {p.product_name || p.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        {!item.product_id && <Input placeholder="Description" value={item.description} onChange={(e) => updateItem(idx, "description", e.target.value)} className="mt-1 rounded-sm h-7 text-xs" />}
                      </td>
                      <td className="p-2"><Input value={item.sku} onChange={(e) => updateItem(idx, "sku", e.target.value)} className="rounded-sm h-8 text-xs" readOnly={!!item.product_id} /></td>
                      <td className="p-2"><Input type="number" min="1" value={item.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)} className="rounded-sm h-8 text-xs text-center" /></td>
                      <td className="p-2"><Input type="number" min="0" step="0.01" value={item.unit_cost} onChange={(e) => updateItem(idx, "unit_cost", e.target.value)} className="rounded-sm h-8 text-xs text-right" /></td>
                      <td className="p-2 text-right text-xs font-medium">{formatCurrency((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_cost) || 0))}</td>
                      <td className="p-2 text-center">
                        {items.length > 1 && <Button variant="ghost" size="icon" className="h-6 w-6 text-gray-400 hover:text-red-500" onClick={() => removeItem(idx)}><X className="w-3 h-3" /></Button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="p-2 bg-gray-50 border-t">
                <Button variant="ghost" size="sm" onClick={addItem} className="text-xs text-[#E63946] hover:text-[#C1121F]"><Plus className="w-3 h-3 mr-1" /> Add Item</Button>
              </div>
            </div>
            <div className="flex justify-end mt-3">
              <div className="text-right space-y-1">
                <p className="text-xs text-gray-500">Subtotal: {formatCurrency(subtotal)}</p>
                <p className="text-xs text-gray-500">VAT ({form.vat_rate}%): {formatCurrency(vatAmount)}</p>
                <p className="text-base font-bold text-[#E63946]">Total: {formatCurrency(total)}</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold uppercase text-gray-500 mb-1 block">Delivery Address</label>
              <textarea value={form.delivery_address} onChange={(e) => setForm({ ...form, delivery_address: e.target.value })}
                className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm h-16 resize-none focus:outline-none focus:ring-2 focus:ring-[#E63946]" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase text-gray-500 mb-1 block">Notes to Supplier</label>
              <textarea value={form.notes_to_supplier} onChange={(e) => setForm({ ...form, notes_to_supplier: e.target.value })}
                className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm h-16 resize-none focus:outline-none focus:ring-2 focus:ring-[#E63946]" />
            </div>
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button data-testid="save-po-btn" onClick={handleSubmit} disabled={saving} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />} Create PO
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== PO Detail ====================

function PODetail({ poId }) {
  const [po, setPo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isManager = ["admin", "sales_manager"].includes(user?.role);

  const fetchPO = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/purchase-orders/${poId}`);
      setPo(res.data);
    } catch { toast.error("Failed to load PO"); navigate("/purchase-orders"); }
    setLoading(false);
  }, [poId, navigate]);

  useEffect(() => { fetchPO(); }, [fetchPO]);

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      const res = await api.get(`/purchase-orders/${poId}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `${po.po_number}.pdf`; a.click();
    } catch { toast.error("PDF generation failed"); }
    setPdfLoading(false);
  };

  const handleSend = async () => {
    try {
      const res = await api.post(`/purchase-orders/${poId}/send`);
      setPo(res.data);
      toast.success(`${po.po_number} marked as sent`);
      setSendModalOpen(false);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleCancel = async () => {
    if (!cancelReason.trim()) { toast.error("Reason required"); return; }
    try {
      const res = await api.post(`/purchase-orders/${poId}/cancel`, { reason: cancelReason });
      setPo(res.data);
      toast.success("PO cancelled");
      setCancelOpen(false);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/purchase-orders/${poId}`);
      toast.success("PO deleted");
      navigate("/purchase-orders");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  if (loading || !po) return <div className="flex items-center justify-center min-h-[400px]"><Loader2 className="w-6 h-6 animate-spin text-[#E63946]" /></div>;

  const isOverdue = po.status === "Sent" && po.expected_delivery_date && po.expected_delivery_date < new Date().toISOString().split("T")[0];
  const daysOverdue = isOverdue ? Math.floor((new Date() - new Date(po.expected_delivery_date)) / 86400000) : 0;
  const totalOrdered = po.line_items.reduce((s, i) => s + i.quantity, 0);
  const totalReceived = po.line_items.reduce((s, i) => s + (i.qty_received || 0), 0);
  const receivingPct = totalOrdered > 0 ? Math.round((totalReceived / totalOrdered) * 100) : 0;

  const StatusIcon = statusIcons[po.status] || Package;

  return (
    <div data-testid="po-detail-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate("/purchase-orders")}><ArrowLeft className="w-4 h-4" /></Button>
          <div>
            <h1 className="font-heading text-xl font-bold text-[#1F2937] flex items-center gap-2">
              {po.po_number}
              <Badge className={`${statusColors[po.status] || ""} text-xs rounded-full border px-2 py-0.5`}>{po.status}</Badge>
              {po.email_sent && <Badge className="rounded-full text-xs bg-green-50 text-green-700 border border-green-200"><Mail className="w-3 h-3 mr-1 inline" />Emailed</Badge>}
            </h1>
            <p className="text-sm text-gray-500">{po.supplier_name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button data-testid="download-po-pdf" variant="outline" size="sm" onClick={handleDownloadPdf} disabled={pdfLoading} className="rounded-sm">
            {pdfLoading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />} PDF
          </Button>
          {po.status === "Draft" && isManager && (
            <>
              <Button variant="outline" size="sm" onClick={() => setEditOpen(true)} className="rounded-sm"><Pencil className="w-3.5 h-3.5 mr-1" /> Edit</Button>
              <Button size="sm" onClick={() => setSendModalOpen(true)} className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm"><Send className="w-3.5 h-3.5 mr-1" /> Send to Supplier</Button>
              <Button variant="outline" size="sm" onClick={() => setDeleteOpen(true)} className="rounded-sm text-red-600 border-red-200 hover:bg-red-50"><Trash2 className="w-3.5 h-3.5 mr-1" /> Delete</Button>
            </>
          )}
          {po.email_sent && po.status !== "Draft" && isManager && (
            <Button variant="outline" size="sm" onClick={async () => { try { await api.post("/email/resend", { type: "purchase_order", id: po.po_id }); toast.success("Email resent to supplier"); } catch { toast.error("Failed to resend email"); }}} className="rounded-sm text-blue-600 border-blue-200 hover:bg-blue-50"><Mail className="w-3.5 h-3.5 mr-1" /> Resend Email</Button>
          )}
          {["Sent", "Partially Fulfilled"].includes(po.status) && isManager && (
            <Button size="sm" onClick={() => setReceiveOpen(true)} className="bg-green-600 hover:bg-green-700 text-white rounded-sm">
              <PackageCheck className="w-3.5 h-3.5 mr-1" /> {po.status === "Partially Fulfilled" ? "Receive More" : "Receive Items"}
            </Button>
          )}
          {!["Draft", "Cancelled", "Fulfilled"].includes(po.status) && isManager && (
            <Button variant="outline" size="sm" onClick={() => setCancelOpen(true)} className="rounded-sm text-red-600 border-red-200 hover:bg-red-50"><XCircle className="w-3.5 h-3.5 mr-1" /> Cancel PO</Button>
          )}
          {isAdmin && po.status !== "Draft" && (
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)} className="rounded-sm"><Pencil className="w-3.5 h-3.5 mr-1" /> Edit (Admin)</Button>
          )}
        </div>
      </div>

      {isOverdue && (
        <Card className="border-2 border-red-300 bg-red-50 rounded-sm shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <Clock className="w-5 h-5 text-red-600 shrink-0" />
            <div><p className="text-sm font-semibold text-red-800">Delivery Overdue</p>
              <p className="text-xs text-red-700">Expected on {formatDateLong(po.expected_delivery_date)} — overdue by {daysOverdue} day{daysOverdue !== 1 ? "s" : ""}</p></div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="border border-gray-200 rounded-sm shadow-sm">
            <CardContent className="p-5">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                <div><p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">Supplier</p><p className="text-sm font-medium cursor-pointer text-[#E63946] hover:underline" onClick={() => navigate(`/suppliers/${po.supplier_id}`)}>{po.supplier_name}</p></div>
                <div><p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">PO Date</p><p className="text-sm">{formatDateLong(po.po_date)}</p></div>
                <div><p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">Expected Delivery</p><p className={`text-sm ${isOverdue ? "text-red-600 font-semibold" : ""}`}>{po.expected_delivery_date ? formatDateLong(po.expected_delivery_date) : "—"}</p></div>
                <div><p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">Payment Terms</p><p className="text-sm">{po.payment_terms || "—"}</p></div>
              </div>

              {(po.status === "Partially Fulfilled" || po.status === "Fulfilled") && (
                <div className="mb-4">
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                    <span>Receiving Progress</span>
                    <span className="font-semibold">{receivingPct}% ({totalReceived}/{totalOrdered} items)</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className={`h-2 rounded-full transition-all ${receivingPct >= 100 ? "bg-green-500" : "bg-amber-500"}`} style={{ width: `${receivingPct}%` }} />
                  </div>
                </div>
              )}

              <table className="w-full text-sm">
                <thead><tr className="border-b border-gray-200">
                  <th className="pb-2 text-left text-xs text-gray-500">SKU</th>
                  <th className="pb-2 text-left text-xs text-gray-500">Description</th>
                  <th className="pb-2 text-center text-xs text-gray-500">Ordered</th>
                  <th className="pb-2 text-center text-xs text-gray-500">Received</th>
                  <th className="pb-2 text-center text-xs text-gray-500">Pending</th>
                  <th className="pb-2 text-right text-xs text-gray-500">Unit Cost</th>
                  <th className="pb-2 text-right text-xs text-gray-500">Total</th>
                  <th className="pb-2 text-center text-xs text-gray-500">Status</th>
                </tr></thead>
                <tbody>
                  {po.line_items.map((item) => {
                    const received = item.qty_received || 0;
                    const pending = item.quantity - received;
                    const itemStatus = received === 0 ? "Pending" : received >= item.quantity ? "Received" : "Partial";
                    return (
                      <tr key={item.item_id} className="border-b border-gray-100">
                        <td className="py-2 text-gray-500 text-xs font-mono">{item.sku || "—"}</td>
                        <td className="py-2">{item.description}</td>
                        <td className="py-2 text-center">{item.quantity}</td>
                        <td className="py-2 text-center font-medium">{received}</td>
                        <td className="py-2 text-center text-gray-500">{pending}</td>
                        <td className="py-2 text-right">{formatCurrency(item.unit_cost)}</td>
                        <td className="py-2 text-right font-medium">{formatCurrency(item.line_total)}</td>
                        <td className="py-2 text-center">
                          <Badge className={`text-[10px] rounded-full px-1.5 py-0 ${
                            itemStatus === "Received" ? "bg-green-50 text-green-700" :
                            itemStatus === "Partial" ? "bg-amber-50 text-amber-700" :
                            "bg-gray-100 text-gray-500"}`}>{itemStatus}</Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="flex justify-end mt-4 space-y-1 text-right">
                <div>
                  <p className="text-xs text-gray-500">Subtotal: {formatCurrency(po.subtotal)}</p>
                  <p className="text-xs text-gray-500">VAT ({po.vat_rate}%): {formatCurrency(po.vat_amount)}</p>
                  <p className="text-lg font-bold text-[#E63946] mt-1">Total: {formatCurrency(po.total)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {po.notes_to_supplier && (
            <Card className="border border-gray-200 rounded-sm shadow-sm">
              <CardContent className="p-4"><p className="text-xs font-semibold uppercase text-gray-400 mb-1">Notes to Supplier</p><p className="text-sm text-gray-700">{po.notes_to_supplier}</p></CardContent>
            </Card>
          )}

          {po.receiving_history?.length > 0 && (
            <Card className="border border-gray-200 rounded-sm shadow-sm">
              <CardContent className="p-5">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2"><Truck className="w-4 h-4" /> Receiving History</h3>
                <div className="space-y-3">
                  {po.receiving_history.map((rcv, idx) => (
                    <div key={idx} className="border border-gray-100 rounded-sm p-3 bg-gray-50/50">
                      <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                        <span className="font-medium text-gray-700">{formatDateLong(rcv.delivery_date)}</span>
                        <span>Received by {rcv.received_by_name}</span>
                      </div>
                      {rcv.delivery_note && <p className="text-xs text-gray-500 mb-1">Ref: {rcv.delivery_note}</p>}
                      <div className="flex flex-wrap gap-2">
                        {rcv.items.map((ri, j) => (
                          <Badge key={j} className="bg-green-50 text-green-700 text-[10px] rounded-sm">{ri.qty_received}x {ri.description}</Badge>
                        ))}
                      </div>
                      {rcv.notes && <p className="text-xs text-gray-500 mt-1 italic">{rcv.notes}</p>}
                      {rcv.attachments?.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {rcv.attachments.map((att, k) => (
                            <a key={k} href={att.url} target="_blank" rel="noreferrer" className="text-[10px] text-blue-600 hover:underline bg-blue-50 rounded px-1.5 py-0.5 inline-flex items-center gap-1">
                              <FileText className="w-2.5 h-2.5" />{att.filename}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="space-y-4">
          <Card className="border border-gray-200 rounded-sm shadow-sm">
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Summary</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Status</span>
                  <Badge className={`${statusColors[po.status] || ""} text-xs rounded-full border`}>{po.status}</Badge>
                </div>
                <div className="flex items-center justify-between text-sm"><span className="text-gray-500">Items</span><span className="font-medium">{po.line_items.length}</span></div>
                <div className="flex items-center justify-between text-sm"><span className="text-gray-500">Total</span><span className="font-bold text-[#E63946]">{formatCurrency(po.total)}</span></div>
                {po.created_by_name && <div className="flex items-center justify-between text-sm"><span className="text-gray-500">Created by</span><span>{po.created_by_name}</span></div>}
              </div>
            </CardContent>
          </Card>

          {po.delivery_address && (
            <Card className="border border-gray-200 rounded-sm shadow-sm">
              <CardContent className="p-4"><p className="text-xs font-semibold uppercase text-gray-400 mb-1">Delivery Address</p><p className="text-sm text-gray-700 whitespace-pre-line">{po.delivery_address}</p></CardContent>
            </Card>
          )}

          {/* Linked Expense */}
          {po.linked_expense_id ? (
            <Card className="border border-green-200 rounded-sm shadow-sm bg-green-50/30">
              <CardContent className="p-4">
                <p className="text-xs font-semibold uppercase text-gray-400 mb-2">Related Expense</p>
                <div className="flex items-center justify-between">
                  <span className="font-mono font-semibold text-[#E63946] text-sm">{po.linked_expense_number}</span>
                  <Button variant="outline" size="sm" className="rounded-sm text-xs h-7" onClick={() => navigate("/expenses")}>
                    <ExternalLink className="w-3 h-3 mr-1" /> View
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (["Fulfilled", "Partially Fulfilled"].includes(po.status) && isManager && (
            <Card className="border border-dashed border-gray-300 rounded-sm shadow-sm">
              <CardContent className="p-4">
                <p className="text-xs font-semibold uppercase text-gray-400 mb-2">Create Expense</p>
                <p className="text-xs text-gray-500 mb-2">Create an expense record from this PO's supplier invoice.</p>
                <Button data-testid="create-expense-from-po" size="sm" variant="outline" className="rounded-sm w-full text-xs"
                  onClick={async () => {
                    try {
                      const res = await api.post(`/purchase-orders/${poId}/create-expense`);
                      toast.success(`Expense ${res.data.expense_number} created from PO`);
                      fetchPO();
                    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
                  }}>
                  <Receipt className="w-3.5 h-3.5 mr-1" /> Create Expense from PO
                </Button>
              </CardContent>
            </Card>
          ))}

          <Card className="border border-gray-200 rounded-sm shadow-sm">
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Audit Trail</h3>
              <AuditTrail poId={poId} />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Send Modal */}
      <Dialog open={sendModalOpen} onOpenChange={setSendModalOpen}>
        <DialogContent className="max-w-lg rounded-sm">
          <DialogHeader><DialogTitle>Send PO to Supplier</DialogTitle></DialogHeader>
          <div className="space-y-4 text-sm">
            <div className="bg-gray-50 rounded-sm p-4 space-y-2">
              <p className="font-semibold">Instructions:</p>
              <ol className="list-decimal pl-4 space-y-1 text-gray-600">
                <li>Click "Download PDF" below to get the PO document</li>
                <li>Click "Open Email" to compose an email with the PO details</li>
                <li>Attach the downloaded PDF to the email and send</li>
                <li>Click "Mark as Sent" to update the PO status</li>
              </ol>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleDownloadPdf} disabled={pdfLoading} className="rounded-sm flex-1">
                <Download className="w-3.5 h-3.5 mr-1" /> Download PDF
              </Button>
              <Button variant="outline" size="sm" className="rounded-sm flex-1"
                onClick={() => {
                  const subject = encodeURIComponent(`Purchase Order ${po.po_number}`);
                  const body = encodeURIComponent(`Dear ${po.supplier_name},\n\nPlease find attached Purchase Order ${po.po_number} for your review.\n\nTotal: R${po.total.toFixed(2)}\n\nKind regards`);
                  window.open(`mailto:?subject=${subject}&body=${body}`);
                }}>
                <MailOpen className="w-3.5 h-3.5 mr-1" /> Open Email
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendModalOpen(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="mark-po-sent" onClick={handleSend} className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm">
              <Check className="w-3.5 h-3.5 mr-1" /> Mark as Sent
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Dialog */}
      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Cancel Purchase Order?</AlertDialogTitle>
            <AlertDialogDescription>
              <p className="text-sm text-gray-500 mb-2">Please provide a reason for cancellation.</p>
              <textarea data-testid="cancel-reason" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Reason for cancellation..."
                className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm h-20 resize-none focus:outline-none focus:ring-2 focus:ring-[#E63946]" />
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Go Back</AlertDialogCancel>
            <AlertDialogAction onClick={handleCancel} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">Cancel PO</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete Purchase Order?</AlertDialogTitle><AlertDialogDescription>This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Dialog */}
      {editOpen && <EditPODialog po={po} open={editOpen} onClose={() => setEditOpen(false)} onSaved={(updated) => { setPo(updated); setEditOpen(false); }} />}

      {/* Receive Items Dialog */}
      {receiveOpen && <ReceiveItemsDialog po={po} open={receiveOpen} onClose={() => setReceiveOpen(false)} onReceived={(result) => { setPo(result.purchase_order); setReceiveOpen(false); }} />}
    </div>
  );
}

// ==================== Edit PO Dialog ====================

function EditPODialog({ po, open, onClose, onSaved }) {
  const [form, setForm] = useState({
    expected_delivery_date: po.expected_delivery_date || "",
    delivery_address: po.delivery_address || "",
    delivery_instructions: po.delivery_instructions || "",
    payment_terms: po.payment_terms || "",
    notes_to_supplier: po.notes_to_supplier || "",
    internal_notes: po.internal_notes || "",
  });
  const [items, setItems] = useState(po.line_items.map((i) => ({ ...i })));
  const [products, setProducts] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/products?limit=500").then((r) => setProducts(r.data.products)); }, []);

  const addItem = () => setItems([...items, { item_id: "", description: "", sku: "", product_id: "", quantity: 1, unit_cost: 0, qty_received: 0 }]);
  const removeItem = (idx) => items.length > 1 && setItems(items.filter((_, i) => i !== idx));
  const updateItem = (idx, field, val) => { const u = [...items]; u[idx] = { ...u[idx], [field]: val }; setItems(u); };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/purchase-orders/${po.po_id}`, { ...form, line_items: items, vat_rate: po.vat_rate });
      toast.success("PO updated");
      onSaved(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto rounded-sm">
        <DialogHeader><DialogTitle>Edit {po.po_number}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><label className="text-xs font-semibold uppercase text-gray-500 mb-1 block">Expected Delivery</label>
              <Input type="date" value={form.expected_delivery_date} onChange={(e) => setForm({ ...form, expected_delivery_date: e.target.value })} className="rounded-sm" /></div>
            <div><label className="text-xs font-semibold uppercase text-gray-500 mb-1 block">Payment Terms</label>
              <Input value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} className="rounded-sm" /></div>
          </div>
          {po.status === "Draft" && (
            <div>
              <label className="text-xs font-semibold uppercase text-gray-500 mb-2 block">Line Items</label>
              <table className="w-full text-sm border"><thead><tr className="bg-gray-50 border-b text-xs">
                <th className="p-2 text-left">Description</th><th className="p-2 text-center">Qty</th><th className="p-2 text-right">Unit Cost</th><th className="p-2 w-8"></th>
              </tr></thead><tbody>
                {items.map((item, idx) => (
                  <tr key={idx} className="border-b"><td className="p-2"><Input value={item.description} onChange={(e) => updateItem(idx, "description", e.target.value)} className="rounded-sm h-7 text-xs" /></td>
                    <td className="p-2"><Input type="number" value={item.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)} className="rounded-sm h-7 text-xs text-center" /></td>
                    <td className="p-2"><Input type="number" value={item.unit_cost} onChange={(e) => updateItem(idx, "unit_cost", e.target.value)} className="rounded-sm h-7 text-xs text-right" /></td>
                    <td className="p-2">{items.length > 1 && <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => removeItem(idx)}><X className="w-3 h-3" /></Button>}</td></tr>
                ))}
              </tbody></table>
              <Button variant="ghost" size="sm" onClick={addItem} className="mt-1 text-xs text-[#E63946]"><Plus className="w-3 h-3 mr-1" /> Add</Button>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div><label className="text-xs font-semibold uppercase text-gray-500 mb-1 block">Delivery Address</label>
              <textarea value={form.delivery_address} onChange={(e) => setForm({ ...form, delivery_address: e.target.value })} className="w-full rounded-sm border px-3 py-2 text-sm h-16 resize-none" /></div>
            <div><label className="text-xs font-semibold uppercase text-gray-500 mb-1 block">Notes to Supplier</label>
              <textarea value={form.notes_to_supplier} onChange={(e) => setForm({ ...form, notes_to_supplier: e.target.value })} className="w-full rounded-sm border px-3 py-2 text-sm h-16 resize-none" /></div>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">{saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== Receive Items Dialog ====================

function ReceiveItemsDialog({ po, open, onClose, onReceived }) {
  const [receiveQtys, setReceiveQtys] = useState(
    po.line_items.reduce((acc, item) => ({ ...acc, [item.item_id]: 0 }), {})
  );
  const [deliveryDate, setDeliveryDate] = useState(new Date().toISOString().split("T")[0]);
  const [deliveryNote, setDeliveryNote] = useState("");
  const [notes, setNotes] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    setUploading(true);
    const formData = new FormData();
    for (let f of files) formData.append("files", f);
    try {
      const res = await api.post("/upload-multiple", formData, { headers: { "Content-Type": "multipart/form-data" } });
      setAttachments((prev) => [...prev, ...res.data.files]);
      toast.success(`${res.data.files.length} file(s) uploaded`);
    } catch { toast.error("Upload failed"); }
    setUploading(false);
  };

  const handleReceive = async () => {
    const items = Object.entries(receiveQtys)
      .filter(([, qty]) => qty > 0)
      .map(([item_id, qty]) => ({ item_id, qty_received_now: qty }));
    if (!items.length) { toast.error("Enter quantity for at least one item"); return; }
    if (!deliveryDate) { toast.error("Delivery date required"); return; }
    setSaving(true);
    try {
      const res = await api.post(`/purchase-orders/${po.po_id}/receive`, {
        items, delivery_date: deliveryDate, delivery_note: deliveryNote, notes, attachments,
      });
      const invUpdates = res.data.inventory_updates || [];
      if (invUpdates.length > 0) {
        toast.success(`Items received. Inventory updated for ${invUpdates.length} product${invUpdates.length > 1 ? "s" : ""}.`);
      } else {
        toast.success(res.data.message);
      }
      onReceived(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to receive items"); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto rounded-sm">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><PackageCheck className="w-5 h-5 text-green-600" /> Receive Items — {po.po_number}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-sm p-3 text-sm">
            <span className="text-gray-500">Supplier:</span> <span className="font-medium">{po.supplier_name}</span>
            <span className="text-gray-500 ml-4">PO Date:</span> <span className="font-medium">{formatDateLong(po.po_date)}</span>
          </div>

          <table className="w-full text-sm border border-gray-200 rounded-sm overflow-hidden">
            <thead><tr className="bg-gray-50 border-b">
              <th className="p-2 text-left text-xs text-gray-500">SKU</th>
              <th className="p-2 text-left text-xs text-gray-500">Description</th>
              <th className="p-2 text-center text-xs text-gray-500">Ordered</th>
              <th className="p-2 text-center text-xs text-gray-500">Prev. Received</th>
              <th className="p-2 text-center text-xs text-gray-500 font-bold">Receive Now</th>
              <th className="p-2 text-center text-xs text-gray-500">New Total</th>
            </tr></thead>
            <tbody>
              {po.line_items.map((item) => {
                const prevReceived = item.qty_received || 0;
                const maxQty = item.quantity - prevReceived;
                const qtyNow = receiveQtys[item.item_id] || 0;
                const newTotal = prevReceived + qtyNow;
                const fullyReceived = newTotal >= item.quantity;
                return (
                  <tr key={item.item_id} className={`border-b ${fullyReceived ? "bg-green-50/50" : ""} ${maxQty === 0 ? "opacity-50" : ""}`}>
                    <td className="p-2 text-xs font-mono text-gray-500">{item.sku || "—"}</td>
                    <td className="p-2">{item.description}</td>
                    <td className="p-2 text-center">{item.quantity}</td>
                    <td className="p-2 text-center text-gray-500">{prevReceived}</td>
                    <td className="p-2 text-center">
                      {maxQty > 0 ? (
                        <Input data-testid={`receive-qty-${item.item_id}`}
                          type="number" min="0" max={maxQty}
                          value={qtyNow} onChange={(e) => setReceiveQtys({ ...receiveQtys, [item.item_id]: Math.min(maxQty, Math.max(0, parseInt(e.target.value) || 0)) })}
                          className="w-20 mx-auto rounded-sm h-7 text-xs text-center" />
                      ) : (
                        <Badge className="bg-green-50 text-green-700 text-[10px]">Complete</Badge>
                      )}
                    </td>
                    <td className="p-2 text-center">
                      <span className={fullyReceived ? "text-green-600 font-semibold" : ""}>{newTotal} / {item.quantity}</span>
                      {fullyReceived && <Check className="w-3 h-3 text-green-600 inline ml-1" />}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="grid grid-cols-3 gap-4">
            <div><label className="text-xs font-semibold uppercase text-gray-500 mb-1 block">Delivery Date *</label>
              <Input type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} className="rounded-sm" /></div>
            <div><label className="text-xs font-semibold uppercase text-gray-500 mb-1 block">Delivery Note / Reference</label>
              <Input value={deliveryNote} onChange={(e) => setDeliveryNote(e.target.value)} placeholder="e.g. DN-12345" className="rounded-sm" /></div>
            <div><label className="text-xs font-semibold uppercase text-gray-500 mb-1 block">Notes</label>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any issues, damages..." className="rounded-sm" /></div>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase text-gray-500 mb-1 block">Attach Delivery Note / Packing Slip</label>
            <div className="border border-dashed border-gray-300 rounded-sm p-3">
              <label className="flex items-center justify-center gap-2 cursor-pointer text-sm text-gray-500 hover:text-[#E63946]">
                <Upload className="w-4 h-4" /> {uploading ? "Uploading..." : "Upload files (PDF, Images)"}
                <input type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={handleFileUpload} className="hidden" />
              </label>
              {attachments.length > 0 && (
                <div className="mt-2 space-y-1">
                  {attachments.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-gray-600 bg-gray-50 rounded-sm px-2 py-1">
                      <FileText className="w-3 h-3" /> {f.filename}
                      <Button variant="ghost" size="icon" className="h-4 w-4 ml-auto" onClick={() => setAttachments(attachments.filter((_, j) => j !== i))}><X className="w-3 h-3" /></Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button data-testid="confirm-receive-btn" onClick={handleReceive} disabled={saving} className="bg-green-600 hover:bg-green-700 text-white rounded-sm">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <PackageCheck className="w-4 h-4 mr-1" />} Receive Items
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== Audit Trail ====================

function AuditTrail({ poId }) {
  const [entries, setEntries] = useState([]);
  useEffect(() => {
    api.get(`/audit-trail?entity_type=purchase_order&entity_id=${poId}`)
      .then((r) => setEntries(r.data.entries || []))
      .catch(() => {});
  }, [poId]);

  if (!entries.length) return <p className="text-xs text-gray-400">No activity yet</p>;
  return (
    <div className="space-y-2">
      {entries.slice(0, 10).map((e, i) => (
        <div key={i} className="flex items-start gap-2">
          <div className="w-1.5 h-1.5 rounded-full bg-[#E63946] mt-1.5 shrink-0" />
          <div><p className="text-xs text-gray-700">{e.details}</p>
            <p className="text-[10px] text-gray-400">{e.user_name} — {formatDateLong(e.created_at)}</p></div>
        </div>
      ))}
    </div>
  );
}
